/* ============================================================================
 * NRN PRICE SYNC — instant, flash-free variant price update
 * ----------------------------------------------------------------------------
 * Replaces prices cleanly without page refresh. No digit animation —
 * just an instant clean swap. If value is identical, does nothing at all.
 * Compare-at price uses the real variant.compare_at_price from Shopify JSON.
 * ==========================================================================*/
(function () {
  'use strict';

  /* Strip a money string like "€1.234,56" or "$1,234.56" down to integer cents. */
  function parseMoneyCents(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^\d.,-]/g, '');
    if (!cleaned) return null;
    var lastDot = cleaned.lastIndexOf('.');
    var lastComma = cleaned.lastIndexOf(',');
    var decIdx = Math.max(lastDot, lastComma);
    var intPart, fracPart;
    if (decIdx === -1) {
      intPart = cleaned;
      fracPart = '00';
    } else {
      intPart = cleaned.slice(0, decIdx).replace(/[.,]/g, '');
      fracPart = cleaned.slice(decIdx + 1);
      if (fracPart.length === 0) fracPart = '00';
      else if (fracPart.length === 1) fracPart = fracPart + '0';
      else if (fracPart.length > 2) {
        intPart = (intPart + fracPart).replace(/[.,]/g, '');
        fracPart = '00';
      }
    }
    var sign = intPart.indexOf('-') !== -1 ? -1 : 1;
    intPart = intPart.replace(/-/g, '');
    var num = parseInt(intPart || '0', 10) * 100 + parseInt(fracPart || '0', 10);
    return sign * num;
  }

  /* Format cents using a template string to preserve currency symbol + separators */
  function formatLikeTemplate(template, cents) {
    if (!template) return (cents / 100).toFixed(2);
    var negative = cents < 0;
    var abs = Math.abs(cents);
    var intVal = Math.floor(abs / 100);
    var fracVal = abs % 100;

    var lastDot = template.lastIndexOf('.');
    var lastComma = template.lastIndexOf(',');
    var decIdx = Math.max(lastDot, lastComma);
    var decSep = decIdx === lastDot ? '.' : ',';
    var thouSep = decSep === '.' ? ',' : '.';
    var hasDecimal = decIdx !== -1;

    var check = decIdx !== -1 ? template.slice(decIdx + 1).replace(/[^\d]/g, '') : '';
    var useDecimals = hasDecimal && check.length <= 2;

    var intStr = String(intVal).replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    var result;
    if (useDecimals) {
      var fracStr = fracVal < 10 ? '0' + fracVal : String(fracVal);
      result = intStr + decSep + fracStr;
    } else {
      result = intStr;
    }

    // Reconstruct with same prefix/suffix as template
    var tplDigits = template.match(/[\d.,]+/);
    if (!tplDigits) return result;
    var beforeNum = template.slice(0, template.indexOf(tplDigits[0]));
    var afterNum  = template.slice(template.indexOf(tplDigits[0]) + tplDigits[0].length);
    return (negative ? '-' : '') + beforeNum + result + afterNum;
  }

  /* Find the leaf element containing the money amount */
  function findMoneyEl(scope) {
    if (!scope) return null;
    var preferred = scope.querySelectorAll(
      '.price-item--last, .price__sale .price-item--sale, .price__regular .price-item--regular, .price-item--sale, .price-item--regular'
    );
    for (var i = 0; i < preferred.length; i++) {
      var el = preferred[i];
      if (/\d/.test(el.textContent || '')) return el;
    }
    var all = scope.querySelectorAll('span, div');
    for (var j = 0; j < all.length; j++) {
      if (/\d/.test(all[j].textContent || '') && all[j].children.length === 0) return all[j];
    }
    return null;
  }

  /* Update one price wrapper in place. Returns true if anything was changed. */
  function updatePriceSlot(destWrap, srcWrap) {
    if (!destWrap || !srcWrap) return false;

    // ── On-sale class toggle
    var destPrice = destWrap.querySelector('.price');
    var srcPrice  = srcWrap.querySelector('.price');
    if (destPrice && srcPrice) {
      ['price--on-sale', 'price--sold-out', 'price--no-compare'].forEach(function (c) {
        var should = srcPrice.classList.contains(c);
        if (destPrice.classList.contains(c) !== should) {
          destPrice.classList.toggle(c, should);
        }
      });
    }

    // ── Main sale price — instant swap, no animation
    var destMoneyEl = findMoneyEl(destWrap);
    var srcMoneyEl  = findMoneyEl(srcWrap);
    var changed = false;
    if (destMoneyEl && srcMoneyEl) {
      var fromCents = parseMoneyCents(destMoneyEl.textContent);
      var toCents   = parseMoneyCents(srcMoneyEl.textContent);
      if (fromCents !== null && toCents !== null && fromCents !== toCents) {
        // Instant clean swap — no tween
        destMoneyEl.textContent = srcMoneyEl.textContent;
        changed = true;
      } else if (toCents !== null && fromCents === null) {
        destMoneyEl.textContent = srcMoneyEl.textContent;
        changed = true;
      }
    } else if (srcMoneyEl && !destMoneyEl) {
      destWrap.innerHTML = srcWrap.innerHTML;
      return true;
    }

    // ── Compare-at price (the strikethrough)
    var destWas    = destWrap.querySelector('.nrn-was-price__label');
    var srcWas     = srcWrap.querySelector('.nrn-was-price__label');
    var destWasWrap = destWrap.querySelector('.nrn-was-price');
    var srcWasWrap  = srcWrap.querySelector('.nrn-was-price');

    if (srcWas && destWas) {
      var wasFrom = parseMoneyCents(destWas.textContent);
      var wasTo   = parseMoneyCents(srcWas.textContent);
      if (wasFrom !== null && wasTo !== null && wasFrom !== wasTo) {
        destWas.textContent = srcWas.textContent;
        changed = true;
      }
    } else if (srcWasWrap && !destWasWrap) {
      destWrap.appendChild(srcWasWrap.cloneNode(true));
      changed = true;
    } else if (!srcWasWrap && destWasWrap) {
      destWasWrap.remove();
      changed = true;
    }

    return changed;
  }

  /* Format a Shopify money integer (cents) like "€19,99" or "$19.99"
     by detecting locale from an existing price element on the page. */
  function formatShopifyMoney(cents) {
    if (cents === null || cents === undefined) return null;
    // Try to find an existing formatted price to use as template.
    // NRN FIX: frueher wurde stur das ERSTE .price-item genommen. War das
    // gerade leer (versteckter Slot), fiel die Formatierung auf "12.99" ohne
    // Waehrungszeichen zurueck. Jetzt wird das erste Element MIT Ziffern
    // gesucht.
    var template = null;
    var cands = document.querySelectorAll('.price-item, .nrn-was-price__label');
    for (var ti = 0; ti < cands.length; ti++) {
      var txt = (cands[ti].textContent || '').trim();
      if (txt && /\d/.test(txt)) { template = txt; break; }
    }
    if (!template || !/\d/.test(template)) {
      // Fallback: plain division
      return (cents / 100).toFixed(2);
    }
    return formatLikeTemplate(template, cents);
  }

  window.NrnPriceSync = {
    update: updatePriceSlot,

    /* Update main + sticky-mobile + sticky-desktop price slots.
       Called with the source HTML string from Shopify's section rendering.
       Also surgically syncs .nrn-was-inline (the compare-at price shown beside
       the main price) so the badge script's MutationObserver never needs to
       remove+re-add it — eliminating the compare-at price flicker. */
    updateAll: function (sourceHtml, srcSectionId, destSectionId) {
      var slots = [
        ['price', 'price'],
        ['price-mobile', 'price-mobile'],
        ['price-desktop', 'price-desktop'],
      ];
      slots.forEach(function (pair) {
        var srcId  = pair[0] + '-' + srcSectionId;
        var destId = pair[1] + '-' + destSectionId;
        var src  = sourceHtml.getElementById(srcId);
        var dest = document.getElementById(destId);
        if (src && dest) {
          updatePriceSlot(dest, src);

          // NRN FIX: also sync the .nrn-was-inline element that lives OUTSIDE
          // the price div (injected by the sale-badge custom_liquid block).
          // We update it surgically so the MutationObserver guard keeps working.
          var row = dest.parentNode;
          if (row) {
            var inlineWas = row.querySelector('.nrn-was-inline');
            var srcWasLabel = src.querySelector('.nrn-was-price .nrn-was-price__label');
            if (inlineWas && srcWasLabel) {
              var newText = srcWasLabel.textContent.trim();
              if (inlineWas.textContent.trim() !== newText) {
                inlineWas.textContent = newText;
              }
            } else if (!srcWasLabel && inlineWas) {
              // variant has no compare-at price → remove the inline element
              inlineWas.remove();
            }
            // If srcWasLabel exists but inlineWas doesn't: let the badge
            // script's variant:change listener handle creation (no DOM flash).
          }
        }
      });
    },

    /* Direct variant-based update: uses variant.price and variant.compare_at_price
       directly from Shopify's variant JSON — always the real, correct values.
       Updates BOTH the regular slot and the sale slot so the correct one is
       visible regardless of which CSS class is active. */
    updateFromVariant: function (variant, sectionId) {
      if (!variant) return;
      var selectors = [
        '#price-' + sectionId,
        '#price-mobile-' + sectionId,
        '#price-desktop-' + sectionId,
      ];
      var newPriceCents = variant.price;
      var newPriceStr   = formatShopifyMoney(newPriceCents);
      var compareAtCents = variant.compare_at_price || null;
      var isOnSale = compareAtCents && compareAtCents > newPriceCents;

      selectors.forEach(function (sel) {
        var wrap = document.querySelector(sel);
        if (!wrap) return;

        var priceClass = wrap.querySelector('.price');

        // Update the regular price slot (shown when NOT on sale)
        var regularEl = wrap.querySelector('.price__regular .price-item--regular');
        if (regularEl && newPriceStr) {
          var curCents = parseMoneyCents(regularEl.textContent);
          if (curCents !== newPriceCents) {
            regularEl.textContent = newPriceStr;
          }
        }

        // Update the sale price slot (shown when ON sale)
        var saleEl = wrap.querySelector('.price__sale .price-item--sale, .price__sale .price-item--last');
        if (saleEl && newPriceStr) {
          var curSaleCents = parseMoneyCents(saleEl.textContent);
          if (curSaleCents !== newPriceCents) {
            saleEl.textContent = newPriceStr;
          }
        }

        // Toggle on-sale state
        if (priceClass) {
          if (isOnSale) {
            priceClass.classList.add('price--on-sale');
            priceClass.classList.remove('price--no-compare');
          } else {
            priceClass.classList.remove('price--on-sale');
            priceClass.classList.add('price--no-compare');
          }

          // NRN FIX: andere Skripte setzen display:none INLINE (!important) auf
          // .price__regular bzw. .price__sale und nehmen es nie zurueck. Beim
          // Wechsel sale <-> nicht-sale waren dadurch beide Slots versteckt und
          // die Preis-Row leer. Hier immer beide Seiten explizit setzen.
          var regWrap  = wrap.querySelector('.price__regular');
          var saleWrap = wrap.querySelector('.price__sale');
          if (regWrap) {
            regWrap.style.setProperty('display', isOnSale ? 'none' : 'block', 'important');
          }
          if (saleWrap) {
            if (isOnSale) {
              saleWrap.style.setProperty('display', 'flex', 'important');
              saleWrap.style.setProperty('flex-direction', 'row', 'important');
              saleWrap.style.setProperty('align-items', 'baseline', 'important');
            } else {
              saleWrap.style.setProperty('display', 'none', 'important');
            }
          }
        }

        // Compare-at price (the strikethrough)
        var compareAtCents = variant.compare_at_price || null;
        var wasWrap = wrap.querySelector('.nrn-was-price');
        var wasEl   = wrap.querySelector('.nrn-was-price__label');

        if (isOnSale) {
          var compareStr = formatShopifyMoney(compareAtCents);
          if (wasEl) {
            var curWasCents = parseMoneyCents(wasEl.textContent);
            if (curWasCents !== compareAtCents) {
              wasEl.textContent = compareStr;
            }
          }
          // If was-price wrapper doesn't exist yet, create it
          if (!wasWrap && compareStr) {
            var newWas = document.createElement('div');
            newWas.className = 'nrn-was-price nrn-was-inline';
            newWas.innerHTML = '<s class="nrn-was-price__label">' + compareStr + '</s>';
            wrap.appendChild(newWas);
          }
        } else {
          // NRN FIX: den Wrapper NICHT entfernen. In <div class="nrn-was-price">
          // steckt ein <style>-Block aus snippets/price.liquid mit
          // Preis-Grundregeln. Wurde er geloescht, war dieses CSS fuer den Rest
          // der Sitzung weg und die Preis-Darstellung brach zusammen.
          // Stattdessen nur den Text leeren — das Badge-Skript prueft auf
          // leeren Text und injiziert dann keinen Streichpreis.
          if (wasEl) wasEl.textContent = '';
        }
      });
    },
  };

  /* ── Sticky ATC: option labels + variant image (no innerHTML, no flash) ── */
  window.NrnStickyAtcSync = {
    update: function (variant) {
      if (!variant) return;

      var optionValueEls = document.querySelectorAll('.sticky-atc .sticky-atc-active-variant__option-value');
      var opts = variant.options || [];
      if (optionValueEls.length && opts.length) {
        var perInstance = optionValueEls.length / opts.length;
        if (perInstance >= 1 && perInstance === Math.floor(perInstance)) {
          optionValueEls.forEach(function (el, i) {
            var newVal = opts[i % opts.length];
            var hasSlash = (el.textContent || '').indexOf('/') !== -1;
            var nextText = hasSlash ? newVal + ' /' : newVal;
            if ((el.textContent || '').trim().replace(/\s+/g, ' ') !== nextText.trim().replace(/\s+/g, ' ')) {
              el.textContent = nextText;
            }
          });
        }
      }

      var imgUrl = variant.featured_image && variant.featured_image.src;
      if (imgUrl) {
        var stickyImgs = document.querySelectorAll('.sticky-atc .sticky-atc__variant-image, .sticky-atc .sticky-atc__product-image');
        stickyImgs.forEach(function (img) {
          var base = imgUrl.split('?')[0];
          var cur  = (img.src || '').split('?')[0];
          if (cur !== base) img.src = imgUrl;
        });
      }
    },
  };

})();
